/**
 * @file The single copy deck for Form Derived Values — every user-facing string
 * of the SYD-2404 spec §12, plus the in-wireframe strings §3–§11 render.
 * Consumers: the Derived values pane and drawer, `FieldLabel`'s derived popover,
 * the projected-cell surfaces and the bulk-path skip reports. Invariants: the
 * strings are verbatim spec copy in sentence case — never re-punctuate or
 * shorten them; the word "formula" describes only the ArTable mechanism and
 * never a derived value; interpolating strings are functions of named values so
 * a caller cannot reassemble a sentence in its own order.
 */
import type { FormDeriveSkipReason } from '#types';

/** Fixed filler-safe wording for the two reasons the engine records. */
const skipReason: Record<FormDeriveSkipReason, string> = Object.freeze({
  targetFormulaOwned: 'A formula owns this column',
  cycle: 'Part of a loop',
});

/**
 * One banner per distinct config-level problem, in the shape spec §3 note 3
 * gives: the derives named, then what is wrong as a noun phrase, then the fix.
 * The noun phrase is plural-neutral on purpose — one banner covers every derive
 * carrying that problem, and "Unit cost, Quote total: a formula on the table
 * already calculates this column." has to read as well as the singular does.
 * Keyed by `FormDeriveDraftProblem['kind']` plus the two statuses spec §3 calls
 * "Not calculating", which are not problems but do need the same banner.
 */
const problemBanner = Object.freeze({
  invalidDeriveId: {
    statement: 'no usable identifier',
    fix: 'Delete it and add it again.',
  },
  duplicateDeriveId: {
    statement: 'the same identifier as another derived value',
    fix: 'Duplicate the entry again so it gets its own identifier, or delete one of the two.',
  },
  duplicateTarget: {
    statement: 'a target another derived value already fills',
    fix: 'Change the target, or delete one of the two. One table is filled by one derived value, and every column it needs belongs in that one.',
  },
  dottedTypeId: {
    statement: 'part of a composite column where a whole column is required',
    fix: 'Choose the whole column instead. A part of a value is only legal as a column a derived value fills, as the "Source column" under "A column of the source table", or under "Part of the value".',
  },
  invalidColumnTypeId: {
    statement: 'a column it cannot address',
    fix: 'Choose a column of the table it fills, or a part of a composite column of that table.',
  },
  duplicateColumn: {
    statement: 'the same column filled twice',
    fix: 'Delete one of the two, or point one of them at another column.',
  },
  columnRootOverlap: {
    statement: 'a whole column filled alongside a part of that same column',
    fix: 'Fill either the whole column or its parts, never both — the two write the same cell.',
  },
  emptyColumns: {
    statement: 'no columns to fill',
    fix: 'Open it and add a column for each column of the table it fills.',
  },
  projectionSelfTable: {
    statement: 'a table it both reads and fills',
    fix: 'Choose a different table to fill. Every cell of a table is downstream of the rows a derived value writes there, so the two would form a loop.',
  },
  invalidColumnAlias: {
    statement: 'a column whose name for conditions cannot be used',
    fix: 'Rename it to start with a lower-case letter and carry only letters and digits.',
  },
  duplicateColumnAlias: {
    statement: 'two columns with the same name for conditions',
    fix: 'Rename one of the two.',
  },
  reservedColumnAlias: {
    statement: 'a column named for conditions after a built-in',
    fix: 'Rename it — a name that is already a function or constant is never read.',
  },
  undeclaredGuardAlias: {
    statement: 'a condition reading a column that is not above it',
    fix: 'Move the column it reads higher in the list, and give that column a name for conditions.',
  },
  malformedColumn: {
    statement: 'a column that is not completely configured',
    fix: 'Open it and complete the column, or delete it.',
  },
  unaddressableTableId: {
    statement: 'a table the platform cannot address',
    fix: 'Choose another table.',
  },
  invalidAlias: {
    statement: 'an input whose name cannot be used',
    fix: 'Rename the input to start with a lower-case letter and carry only letters and digits.',
  },
  duplicateAlias: {
    statement: 'the same input name twice',
    fix: 'Rename one of the two inputs.',
  },
  reservedAlias: {
    statement: 'an input named after a built-in',
    fix: 'Rename the input — an input named after a function or constant is never read.',
  },
  expressionUnparsable: {
    statement: 'an expression the platform cannot read',
    fix: 'Open it and build the expression again.',
  },
  undeclaredVariable: {
    statement: 'a name in its expression that is not one of its inputs',
    fix: 'Add the input, or take the name out of the expression.',
  },
  roundToCentsInadmissible: {
    statement: 'a rounding option its kind does not admit',
    fix: 'Open it and save it again to clear the option.',
  },
  malformedTarget: {
    statement: 'no usable target',
    fix: 'Open it and choose a target.',
  },
  malformedEntry: {
    statement: 'an incomplete configuration',
    fix: 'Open it and complete it, or delete it.',
  },
  chainedReferenceHop: {
    statement:
      'a column that reads a record through a reference cell on a table another derived value fills',
    fix: 'Point that column at a column of its own source table, or read the reference from a table no derived value fills. Rows one derived value creates cannot be read through by another.',
  },
  droppedByEngine: {
    statement: 'something the platform refuses to read',
    fix: 'Open it and check every choice in it, or delete it.',
  },
  targetFormulaOwned: {
    statement: "a target the table's formulas already own",
    fix: 'Change the target, or remove the formula on that column.',
  },
  cycle: {
    statement: 'a place in a loop of derived values, so it is not calculated',
    fix: 'Break the loop by changing one target or source.',
  },
});

const formDeriveCopy = Object.freeze({
  tabLabel: 'Derived values',
  addDerivedValue: 'Add derived value',
  paneSubLine:
    'Values calculated by the platform and locked in the form. Evaluation order is worked out from the dependencies, not from this list.',
  emptyStateTitle: 'No derived values on this form type',
  emptyStateBody:
    "A derived value fills a field, or a whole table of rows, from somewhere else in the form — another field, a table total, a property of a referenced record, or one row for each distinct value in a column. The platform keeps it up to date, and people filling the form can't edit it.",
  targetHelper: 'This field becomes read-only in every form of this type.',
  targetTakenBy: (name: string): string => `Already derived by "${name}".`,
  targetFormulaOwned: 'A formula on this table already calculates this column.',
  /**
   * Why a part of a composite value is locked in the composite sidebar: the
   * table's formula sequence targets it. The projection case says nothing here —
   * it carries `cellTooltip`, the sentence the projected cell itself shows, so
   * one derive is never worded two ways between the cell and the sidebar.
   */
  subFieldFormulaOwned: 'A formula on this table calculates this part of the value.',
  editDerivedValue: 'Edit derived value',
  add: 'Add',
  save: 'Save',
  removeInput: 'Remove input',
  targetLabel: 'Target',
  targetPlaceholder: 'Choose a field or a table',
  targetGroupFields: 'Form fields',
  /** A row projection owns the WHOLE table, so the target list offers tables, never columns. */
  targetGroupTables: 'Tables',
  /** Spec §4: crossing the field/table branch cannot keep a source, and says so once. */
  targetBranchCleared:
    'The source was cleared. A field and a table are filled by different kinds of derived value.',
  sourceKindLabel: 'Where the value comes from',
  sourceKindPrompt: 'Choose a target first.',
  /** The segmented control's names, one per shape a derived value can take. */
  sourceKindOption: Object.freeze({
    field: 'Another field',
    colAggregate: 'Total from a table',
    referenceProp: 'From a reference',
    calculation: 'Calculation',
    projection: 'Rows from distinct values',
  }),
  /**
   * The five places ONE column of a filled table reads its value from, keyed by
   * `FormDeriveRowValueSource['kind']`. Consumers: the drawer's per-column value
   * select and `describeFormDeriveRowValue`, so the option a person picks and the
   * phrase the list shows are one wording.
   */
  rowValueKindOption: Object.freeze({
    groupKey: 'The distinct value',
    sourceCell: 'A column of the source table',
    groupAggregate: 'A total for this row',
    rowReferenceProp: 'A property of a referenced record',
    cases: 'The first case that holds',
  }),
  sourceFieldLabel: 'Source field',
  sourceFieldSameAsTarget: 'A field cannot be copied from itself.',
  tableLabel: 'Table',
  columnLabel: 'Column',
  aggregationLabel: 'Aggregation',
  valuePartLabel: 'Part of the value',
  valuePartWholeValue: 'Whole value',
  sourceTableLabel: 'Source table',
  sourceColumnLabel: 'Source column',
  sourceColumnDistinctHelper: 'One row is created for each distinct value in this column.',
  noColumnsAvailable: 'This table has no columns to choose from yet.',

  /** §4 — the column list of a table a derived value fills, and the cases one column can hold. */
  columnsLabel: 'Columns',
  columnsHelper:
    'What this fills on each row it creates. Conditions on a column can read the columns above it.',
  addColumn: 'Add column',
  removeColumn: 'Remove column',
  noColumnsYet: 'No columns yet. Add one for each column of the table this fills.',
  columnTargetLabel: 'Column to fill',
  columnValueLabel: 'Value',
  columnAliasLabel: 'Name for conditions',
  columnAliasHelper: 'Optional. Conditions on the columns below can read this column by this name.',
  casesLabel: 'Cases',
  casesHelper:
    'The first case whose conditions all hold wins. A column that no case matches is left blank.',
  addCase: 'Add case',
  removeCase: 'Remove case',
  caseValueLabel: 'Value',
  caseWhenLabel: 'When',
  caseOtherwise: 'Otherwise',
  addCondition: 'Add condition',
  removeCondition: 'Remove condition',
  conditionColumnLabel: 'Column',
  conditionOperatorLabel: 'Comparison',
  conditionValueLabel: 'Compared with',
  conditionEarlierColumnsOnly:
    'A condition reads only the columns above it, by the name given to each.',
  projectionSameTable:
    'A table cannot be projected into itself. Every cell of a table is downstream of the rows a projection writes, so the two would form a loop and neither would be calculated.',
  columnTypeMismatch: (source: string, column: string): string =>
    `${source} and ${column} hold different kinds of value. The value is converted on the way across, and the result may not be what you expect.`,
  referenceLabel: 'Reference',
  referencePropLabel: 'Property',
  referencePropName: 'Record name',
  referencePropField: 'A field on the record',
  referencePropReference: 'A reference on the record',
  roundToCents: 'Round to cents',
  nameLabel: 'Name',
  nameHelper: 'Optional. The name every message about this derived value uses.',
  notePlaceholder: 'Optional note for administrators',
  noteLabel: 'Note',
  inputsLabel: 'Inputs',
  addInput: 'Add input',
  inputNameLabel: 'Input name',
  inputSourceLabel: 'Input source',
  invalidInputName:
    'An input name starts with a lower-case letter and carries only letters and numbers.',
  duplicateInputName: (name: string): string => `"${name}" is already the name of another input.`,
  unusedInput: (name: string): string => `${name} isn't used in the expression.`,
  expressionLabel: 'Expression',
  expressionScope: 'Only the inputs above can be used.',
  expressionRequired: 'An expression is required.',
  expressionUnreadable: (reason: string): string => `This expression cannot be read: ${reason}`,
  expressionTooComplexForBuilder:
    'This expression is too complex for the builder. Keep editing it in Advanced, or simplify it.',
  expressionUndeclaredVariable: (name: string): string =>
    `"${name}" is not one of the inputs above. Add an input with that name, or change the expression.`,
  builderMode: 'Builder',
  advancedMode: 'Advanced',
  addToken: 'Add',
  expressionModeLabel: 'Expression editor mode',
  numberLabel: 'Number',
  operatorLabel: 'Operator',
  removeToken: 'Remove',
  effectTitle: 'What this will do',
  effectPrompt: 'Choose a target and a source to see what this will do.',
  effectSentence: (name: string, source: string): string => `${name} is ${source}.`,
  effectFieldReadOnly: (name: string): string => `${name} becomes read-only for everyone.`,
  effectFieldReplaced: 'Any value in it today is replaced the next time the form is saved.',
  effectEveryForm: 'Applies to every form of this type, including approved and archived ones.',
  effectCopyLive: 'Updates as soon as the source field changes.',
  effectProjectionDistinctRows: (
    targetTable: string,
    sourceTable: string,
    sourceColumn: string,
  ): string =>
    `The ${targetTable} table gets one row for each distinct ${sourceTable} › ${sourceColumn}. Those rows are managed by this derived value.`,
  effectProjectionLockedColumns: (columns: number): string =>
    columns === 1
      ? 'The one column this fills is locked on managed rows. Other columns stay editable, and people can keep their own notes there.'
      : `The ${columns} columns this fills are locked on managed rows. Other columns stay editable, and people can keep their own notes there.`,
  effectProjectionManualRows:
    'Rows people added themselves are never touched and never de-duplicated against.',
  effectProjectionReorderable: 'Managed rows can be reordered.',
  coercionHelper: 'Blank inputs count as zero; a non-numeric input leaves the field empty.',
  reservedInputName: (name: string): string =>
    `"${name}" is a built-in name. Choose another — an input named after a function or constant is never read.`,
  roundingHelper: 'Applied once to the final total, half away from zero.',
  referenceFreshness:
    'Read from the record each time the form recalculates. Editing the record does not push into forms already saved.',
  projectionDeletionWarningDistinct: (sourceValueNoun: string, sourceTable: string): string =>
    `When a ${sourceValueNoun} stops appearing in ${sourceTable}, its row is deleted — including anything typed into the other columns of that row. If the same ${sourceValueNoun} comes back later, the row returns empty.`,
  cycleModalTitle: 'These derived values depend on each other',
  cycleModalBody: (n: number): string =>
    `${n} derived values form a loop, so none of them can be calculated. Break the loop by changing one target or source, then save again.`,
  deleteTitle: (name: string): string => `Delete "${name}"?`,
  deleteProjectionBody:
    'Every row this derived value created will be deleted from every form of this type — including anything people typed into the other columns of those rows. Rows people added themselves are not affected.',
  deleteProjectionColumnsUnlock: (columns: number): string =>
    columns === 1
      ? 'The one column it filled becomes editable again. Values already in it stay as they are.'
      : `The ${columns} columns it filled become editable again. Values already in them stay as they are.`,
  fieldPopoverTitle: 'Derived value',
  fieldPopoverBody: (sourceSentence: string): string =>
    `Calculated from ${sourceSentence}. It updates on its own and can't be edited here.`,
  fieldPopoverStamp: (relativeTime: string): string => `Last calculated ${relativeTime}`,
  noValueTitle: 'No value',
  noValueBody: "There's nothing to calculate from yet, or one of the inputs isn't a number.",
  skippedToFiller: "This value isn't being kept up to date. Contact your administrator.",
  cellTooltip: (table: string, column: string): string =>
    `Derived from ${table} › ${column}.`,
  managedRowDeleteBlocked: (sourceValueNoun: string, sourceTable: string): string =>
    `This row is managed by a derived value. Remove the ${sourceValueNoun} from ${sourceTable} instead.`,
  /**
   * The reason a bulk delete skipped rows in the `drv~` namespace. It names no
   * particular derived value on purpose: the server refuses on the namespace alone,
   * so a leftover row whose derived value was deleted minutes ago is skipped too,
   * and quoting a configuration that no longer exists would be the wrong reason.
   */
  managedRowsSkipped: (skipped: number): string =>
    `${skipped} ${skipped === 1 ? 'row is' : 'rows are'} managed by a derived value and weren't deleted.`,
  /** Spec §9 wireframe W7's table-header line — the only affordance that explains why some rows behave differently. */
  managedRowSplit: (managed: number, total: number): string =>
    `${managed} of ${total} rows managed by a derived value`,
  /**
   * The same line when the table is paginated. Only the loaded page can be
   * counted client-side, so the sentence says which rows it counted rather than
   * implying a total for the whole table.
   */
  managedRowSplitOnPage: (managed: number, total: number): string =>
    `${managed} of ${total} rows on this page managed by a derived value`,
  pastePartiallySkipped: (pasted: number, total: number, skipped: number): string =>
    `Pasted ${pasted} of ${total} cells. ${skipped} cells are derived and weren't changed.`,
  /**
   * The second sentence of `pastePartiallySkipped`, on its own, for the §9 bulk
   * paths that already open with a count sentence of their own (the optimizer).
   * Unpluralized, exactly as the paste string it is taken from.
   */
  derivedCellsSkipped: (skipped: number): string =>
    `${skipped} cells are derived and weren't changed.`,
  recalculateAllBody: (n: number): string =>
    `${n} forms will be recalculated in the background, including approved and archived ones. Derived values on those forms will be rewritten to match the current configuration. Values people entered themselves are not affected.`,

  /** §3 wireframe W1 — the list's controls, column headers and footer counts. */
  dependencyOrder: 'Dependency order',
  listColumnTarget: 'Target',
  listColumnKind: 'Kind',
  listColumnSource: 'Source',
  listColumnStatus: 'Status',
  edit: 'Edit',
  duplicate: 'Duplicate',
  delete: 'Delete',
  footerSummary: (derives: number, lockedFields: number, tablesReceivingRows: number): string =>
    [
      `${derives} ${derives === 1 ? 'derived value' : 'derived values'}`,
      `${lockedFields} ${lockedFields === 1 ? 'field locked' : 'fields locked'}`,
      `${tablesReceivingRows} ${tablesReceivingRows === 1 ? 'table' : 'tables'} receiving rows`,
    ].join(' · '),

  /** §3 "Status values" — the four states and the words their tooltips carry. */
  statusActive: 'Active',
  statusNotCalculating: 'Not calculating',
  statusInvalid: 'Invalid',
  statusUnsaved: 'Unsaved',
  statusActiveTooltip: 'Valid configuration, and no skip recorded on the versions sampled by diagnostics.',
  statusInvalidTooltip: 'This entry would be dropped when the configuration is read, so it blocks the save.',
  statusUnsavedTooltip: 'Added or edited in this session and not yet saved. No version has run it.',

  /** §3 note 3 — one banner per distinct problem, naming the derives and the fix. */
  problemBannerLine: (names: string[], statement: string): string => {
    const named =
      names.length <= 1
        ? (names[0] ?? '')
        : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    return `${named} ${names.length === 1 ? 'has' : 'have'} ${statement}.`;
  },
  problemBanner,
  tablesNotLoaded:
    'Some of the tables this configuration reads have not loaded yet, so it cannot be checked. Wait for them to load, then save again.',
  /** The same shortfall once loading is over: the tables are not late, they are gone. */
  tablesMissing:
    'Some of the tables this configuration reads no longer exist, so it cannot be checked. Change the derived values that name them.',

  /** §7 — what the toolbar's Save says when it refuses, and the cycle modal's own controls. */
  saveBlockedByProblems:
    "Derived values can't be saved while any of them has a problem. Each one is listed in Derived values with its fix.",
  backToEditing: 'Back to editing',
  /**
   * §7's second blocking modal — "Entry would be dropped by the validator". §12
   * carries no wording for it, so this is written to the deck's own pattern: what
   * is wrong, what it costs, what to do. Pluralized rather than "{n} derived
   * values", because one dropped entry is the common case and the cycle modal's
   * plural-only body cannot be reused (a loop needs at least two members).
   */
  droppedModalTitle: "These derived values can't be saved",
  droppedModalBody: (n: number): string =>
    n === 1
      ? 'One derived value would be dropped when the configuration is read, so nothing was saved. Open it, fix what is listed beside it, then save again.'
      : `${n} derived values would be dropped when the configuration is read, so nothing was saved. Open each one, fix what is listed beside it, then save again.`,
  cycleModalFormulaNote:
    'Table formulas are part of the loop check — a formula on a table can close a loop that the derived values alone would not.',

  /** §7 "Deleting a derive" — the plain confirmation and the row-destroying one. */
  deleteConfirm: 'Delete derived value',
  deleteFieldUnlock: (field: string): string =>
    `The ${field} field becomes editable again. Values already in it stay as they are.`,

  /** §6 — re-pointing a saved projection destroys its managed rows, same modal treatment as delete. */
  projectionRebuildTitle: (name: string): string =>
    `Change the source or target of "${name}"?`,
  projectionRebuildBody: 'Existing managed rows will be deleted and rebuilt.',
  projectionRebuildConfirm: 'Change derived value',

  /** §11 wireframe W8 — the Calculation health section of the pane. */
  calculationHealthTitle: 'Calculation health',
  checkAgain: 'Check again',
  notCheckedYet: 'Not checked yet',
  checkedStamp: (relativeTime: string, rev: number): string =>
    `Checked ${relativeTime} · configuration revision ${rev}`,
  formsOfThisType: 'Forms of this type',
  waitingToRecalculate: 'Waiting to recalculate',
  notBeingCalculated: 'Not being calculated',
  deriveColumnHeader: 'Derived value',
  stateColumnHeader: 'State',
  lastCalculatedColumnHeader: 'Last calculated',
  revisionColumnHeader: 'Configuration revision',
  neverCalculated: 'Never',
  upToDate: 'Up to date',
  /**
   * The behind-count always states its denominator: "12" alone invites the reader
   * to guess the corpus, and the guess is the wrong conclusion this section exists
   * to prevent. `versionsTotal` is the count the same callable measured.
   */
  versionsBehindOfTotal: (behind: number, total: number): string => `${behind} of ${total}`,
  judgedOnSample: (n: number): string =>
    `The states below were read from the ${n} most recently changed forms of this type, not from every form.`,
  checkFailed: 'The check could not be completed. Try again.',
  recalculateAll: 'Recalculate all forms',
  recalculateAllTitle: 'Recalculate every form of this type?',
  recalculateAllConfirm: 'Recalculate',
  cancel: 'Cancel',
  recalculateAllNeedsCheck: 'Check again first, so this can state how many forms it would recalculate.',
  recalculationRunning: (relativeTime: string): string =>
    `A recalculation of this form type is already running — last progress ${relativeTime}.`,
  recalculationQueued: 'Recalculation queued. It runs in the background.',
  recalculationFailed: 'The recalculation could not be started. Try again.',

  /** §11 per-form view — the admin-only entry in an open form's menu and its drawer. */
  perFormMenuEntry: 'Derived values…',
  perFormSubLine:
    'What the platform calculated on this form version, and when. Nobody filling the form sees this.',
  perFormEmpty: 'No derived values on this form type.',
  recalculateThisForm: 'Recalculate this form',
  recalculateThisFormQueued: 'This form will recalculate in the background.',
  recalculateThisFormFailed: 'The recalculation could not be requested. Try again.',
  /** The one refusal that is transient: a computation already holds this version. */
  recalculateThisFormBusy: 'This form is being calculated right now. Try again in a moment.',
  skipReason,
});

export { formDeriveCopy };
